import { Notification } from '../store/notificationStore';
import { detectPositionState, generateNavigationPath } from '../utils/notificationUtils';

export interface NotificationClickAction {
  type: 'navigate' | 'external' | 'modal';
  payload: any;
}

export interface NotificationOptions {
  title: string;
  message: string;
  type: Notification['type'];
  metadata?: Notification['metadata'];
  clickAction?: NotificationClickAction;
  // 🔥 NEW: Event timing support
  eventTimestamp?: number; // When the actual event occurred
  isRecoveryEvent?: boolean; // Whether this is from session recovery
}

/**
 * Central notification management service
 * Transforms SSE events to notification objects and handles click actions
 */
class NotificationService {
  /**
   * Create a notification from SSE event data
   */
  createNotification(options: NotificationOptions): Omit<Notification, 'id' | 'timestamp' | 'isRead'> {
    // For notifications, use more lenient deduplication than toasts
    // Check if this is a test trade (should always be allowed)
    const isTestTrade = options.metadata?.tradeId && 
      (options.metadata.exectype === 'TestTrade' || options.title?.includes('Test'));
    
    if (!isTestTrade) {
      // Use shorter deduplication window for notifications (15 seconds vs 60 for toasts)
      const shouldCreate = this.shouldCreateNotification({
        type: this.mapNotificationTypeToToastType(options.type),
        symbol: options.metadata?.symbol || '',
        account: options.metadata?.account || '',
        timestamp: Date.now(),
        details: {
          pnl: options.metadata?.pnl,
          tradeId: String(options.metadata?.tradeId || ''), // Convert to string for deduplication
          side: options.metadata?.side,
        }
      });

      if (!shouldCreate) {
        throw new Error('Duplicate notification prevented by deduplication service');
      }
    }

    return {
      type: options.type,
      title: options.title,
      message: options.message,
      source: 'sse', // Mark all notifications from this service as SSE notifications
      clickAction: options.clickAction,
      metadata: {
        ...options.metadata,
        // 🔥 NEW: Add event timing metadata
        eventTimestamp: options.eventTimestamp,
        isRecoveryEvent: options.isRecoveryEvent,
      },
    };
  }

  /**
   * Create position opened notification
   */
  createPositionOpenedNotification(data: {
    symbol: string;
    account: string;
    side?: string;
    strategy?: string;
    // 🔥 NEW: Timing support
    eventTimestamp?: number;
    isRecoveryEvent?: boolean;
  }): Omit<Notification, 'id' | 'timestamp' | 'isRead'> {
    
    // Enhanced message format for position opened notifications
    
    return this.createNotification({
      type: 'position_opened',
      title: 'Position Opened',
      message: `🚀 ${data.symbol} position opened`,
      eventTimestamp: data.eventTimestamp,
      isRecoveryEvent: data.isRecoveryEvent,
      metadata: {
        symbol: data.symbol,
        account: data.account,
        side: data.side,
        strategy: data.strategy,
        eventTimestamp: data.eventTimestamp,
        isRecoveryEvent: data.isRecoveryEvent,
      },
      clickAction: {
        type: 'navigate',
        payload: {
          path: data.side && data.account 
            ? `/live-trades/chart/${encodeURIComponent(data.symbol)}/${encodeURIComponent(data.side)}/${encodeURIComponent(data.account)}`
            : `/live-trades?filter=${encodeURIComponent(data.symbol)}`,
          state: { from: '/live-trades' }
        }
      }
    });
  }

  /**
   * Create trade closed notification
   */
  createTradeClosedNotification(data: {
    symbol: string;
    account: string;
    pnl: number;
    tradeId?: string;
    strategy?: string;
    exectype?: string;
    side?: string;
    roi?: number;
    // 🔥 NEW: Timing support  
    eventTimestamp?: number;
    isRecoveryEvent?: boolean;
  }): Omit<Notification, 'id' | 'timestamp' | 'isRead'> {
    const pnlText = data.pnl > 0 ? `+$${data.pnl.toFixed(2)}` : `$${data.pnl.toFixed(2)}`;
    const pnlIcon = data.pnl > 0 ? '🟢' : '🔴';
    const pnlLabel = data.pnl > 0 ? 'Profit' : 'Loss';
    
    return this.createNotification({
      type: 'trade_closed',
      title: `Closed Trade: ${data.symbol}`,
      message: `${pnlIcon} ${pnlLabel} ${pnlText}`,
      eventTimestamp: data.eventTimestamp,
      isRecoveryEvent: data.isRecoveryEvent,
      metadata: {
        symbol: data.symbol,
        account: data.account,
        pnl: data.pnl,
        tradeId: data.tradeId,
        strategy: data.strategy,
        exectype: data.exectype,
        side: data.side,
        roi: data.roi,
        eventTimestamp: data.eventTimestamp,
        isRecoveryEvent: data.isRecoveryEvent,
      },
      clickAction: {
        type: 'navigate',
        payload: {
          path: data.tradeId 
            ? `/closed-trades/chart/${encodeURIComponent(data.symbol)}/${encodeURIComponent(data.account)}/${encodeURIComponent(String(data.tradeId))}`
            : `/closed-trades?filter=${encodeURIComponent(data.symbol)}`,
          state: { from: '/closed-trades' }
        }
      }
    });
  }

  /**
   * Create milestone notification with enhanced direction indicators
   */
  createMilestoneNotification(data: {
    symbol: string;
    account: string;
    side?: string;
    milestone_percentage?: number;
    currentPnl?: number;
    milestone_trend?: 'improving' | 'declining';
    milestone_direction?: string; // Backend navigation context ('to_takeprofit' | 'to_stoploss')
    previous_milestone_direction?: string;
    time_since_last_crossing?: number;
    strategy?: string;
    // 🔥 NEW: Unified notifications support
    milestoneType?: string;
    eventTimestamp?: number;
    isRecoveryEvent?: boolean;
    // 🔥 NEW: Position state detection fields
    tradeId?: string | number;           // Indicates closed position if present
    positionStatus?: 'open' | 'closed';  // Explicit state
  }): Omit<Notification, 'id' | 'timestamp' | 'isRead'> {
    
    // � DEBUG: Log milestone notification data to identify data discrepancies
    console.log(`🎯 [Milestone] Creating notification (${data.isRecoveryEvent ? 'RECOVERY' : 'LIVE'}):`, {
      symbol: data.symbol,
      account: data.account,
      side: data.side || 'MISSING',
      isRecoveryEvent: data.isRecoveryEvent,
      tradeId: data.tradeId || 'MISSING',
      hasRequiredData: !!data.side
    });
    
    // �🔥 FIX: Separate display trend (for icons) from navigation direction (for routing)
    // Use milestone_trend for display indicators (what user sees)
    // Use milestone_direction for navigation context (where to go)
    const displayTrend = data.milestone_trend || 'improving';
    const navigationDirection = data.milestone_direction || 'to_takeprofit';
    
    // 🔥 NEW: Detect position state for proper navigation routing
    const stateInfo = detectPositionState({
      notificationType: 'milestone',
      symbol: data.symbol,
      account: data.account,
      side: data.side,
      tradeId: data.tradeId
    });
    
    // Generate appropriate navigation path based on position state
    const navigation = generateNavigationPath({
      positionStatus: stateInfo.positionStatus,
      symbol: data.symbol,
      account: data.account,
      side: data.side,
      tradeId: data.tradeId
    });
    
    // Handle unified notifications milestone format
    const milestonePercentage = data.milestone_percentage ?? 0;
    
    // 🔥 SIMPLIFIED: Remove frontend deduplication - rely on backend cooldown only
    // Backend already has 30-minute cooldown with enhanced anti-spam protection
    // Having both frontend and backend deduplication creates unnecessary complexity
    
    // Generate direction-aware milestone text with separated concerns
    let milestoneText: string;
    let directionIcon: string;
    let directionText: string;
    
    // Standardized direction indicators with consistent format
    const MILESTONE_DIRECTION_CONFIG = {
      improving: { icon: '📈', text: 'improving', color: 'text-green-400' },
      declining: { icon: '📉', text: 'declining', color: 'text-red-400' }
    } as const;

    const directionConfig = MILESTONE_DIRECTION_CONFIG[displayTrend];
    directionIcon = directionConfig.icon;
    directionText = `(${directionConfig.text})`;
    
    // Generate milestone description with enhanced format for consistency
    
    if (data.milestoneType && data.milestone_percentage === undefined) {
      // Unified notifications format - use generic milestone text
      milestoneText = `${directionIcon} Position Milestone ${directionText}`;
    } else if (milestonePercentage === 0) {
      milestoneText = `${directionIcon} Break Even ${directionText}`;
    } else if (milestonePercentage > 0) {
      milestoneText = `${directionIcon} ${Math.abs(milestonePercentage)}% Profit ${directionText}`;
    } else {
      milestoneText = `${directionIcon} ${Math.abs(milestonePercentage)}% Loss ${directionText}`;
    }

    const pnlText = data.currentPnl ? ` (${data.currentPnl > 0 ? '+' : ''}$${data.currentPnl.toFixed(2)})` : '';
    
    return this.createNotification({
      type: 'milestone',
      title: `Milestone Reached: ${data.symbol}`,
      message: `${data.symbol} ${milestoneText}${pnlText}`,
      eventTimestamp: data.eventTimestamp,
      isRecoveryEvent: data.isRecoveryEvent,
      metadata: {
        symbol: data.symbol,
        account: data.account,
        side: data.side,
        pnl: data.currentPnl,
        strategy: data.strategy,
        milestoneType: data.milestoneType,
        milestone_percentage: data.milestone_percentage, // Add milestone percentage for consistent display
        // Enhanced milestone context for progressive disclosure
        milestone_trend: displayTrend, // Processed display trend ('improving' | 'declining')
        milestone_direction: navigationDirection, // Navigation context ('to_takeprofit' | 'to_stoploss')
        previous_milestone_direction: data.previous_milestone_direction,
        time_since_last_crossing: data.time_since_last_crossing,
        eventTimestamp: data.eventTimestamp,
        isRecoveryEvent: data.isRecoveryEvent,
        // 🔥 NEW: Position state tracking for navigation
        positionStatus: stateInfo.positionStatus,
        isLivePosition: stateInfo.isLivePosition,
        tradeId: data.tradeId,
        navigationType: stateInfo.positionStatus === 'open' ? 'live-chart' : 'closed-chart',
        targetPath: navigation.fullPath,
      },
      clickAction: {
        type: 'navigate',
        payload: {
          path: navigation.fullPath,
          state: navigation.state
        }
      }
    });
  }

  /**
   * Create position closed notification
   */
  createPositionClosedNotification(data: {
    symbol: string;
    account: string;
    side?: string;
    finalPnl?: number;
    strategy?: string;
    tradeId?: string | number;
    eventTimestamp?: number;
    isRecoveryEvent?: boolean;
  }): Omit<Notification, 'id' | 'timestamp' | 'isRead'> {
    
    const pnlText = data.finalPnl ? (data.finalPnl > 0 ? `+$${data.finalPnl.toFixed(2)}` : `$${data.finalPnl.toFixed(2)}`) : '';
    const pnlIcon = data.finalPnl ? (data.finalPnl > 0 ? '🟢' : '🔴') : '';
    const pnlLabel = data.finalPnl ? (data.finalPnl > 0 ? 'Profit' : 'Loss') : 'Closed';
    
    // Generate navigation to closed trades
    const navigation = generateNavigationPath({
      positionStatus: 'closed',
      symbol: data.symbol,
      account: data.account,
      side: data.side,
      tradeId: data.tradeId
    });
    
    return this.createNotification({
      type: 'position_closed',
      title: 'Position Closed',
      message: pnlText ? `${pnlIcon} ${pnlLabel} ${pnlText}` : `${data.symbol} position closed`,
      eventTimestamp: data.eventTimestamp,
      isRecoveryEvent: data.isRecoveryEvent,
      metadata: {
        symbol: data.symbol,
        account: data.account,
        side: data.side,
        pnl: data.finalPnl,
        strategy: data.strategy,
        tradeId: data.tradeId,
        positionStatus: 'closed',
        isLivePosition: false,
        navigationType: 'closed-chart',
        eventTimestamp: data.eventTimestamp,
        isRecoveryEvent: data.isRecoveryEvent,
      },
      clickAction: {
        type: 'navigate',
        payload: {
          path: navigation.fullPath,
          state: navigation.state
        }
      }
    });
  }

  /**
   * Create position modified notification
   */
  createPositionModifiedNotification(data: {
    symbol: string;
    account: string;
    side?: string;
    qtyChange?: number;
    strategy?: string;
    // 🔥 NEW: Unified notifications support
    eventTimestamp?: number;
    isRecoveryEvent?: boolean;
    changes?: any;
  }): Omit<Notification, 'id' | 'timestamp' | 'isRead'> {
    const changeText = data.qtyChange && data.qtyChange > 0 ? 'increased' : 'decreased';
    const qtyText = data.qtyChange ? ` (${changeText})` : '';
    
    return this.createNotification({
      type: 'position_modified',
      title: data.isRecoveryEvent ? 'Position Modified (While Away)' : 'Position Modified',
      message: `🔄 ${data.symbol} position modified${qtyText}`,
      eventTimestamp: data.eventTimestamp,
      isRecoveryEvent: data.isRecoveryEvent,
      metadata: {
        symbol: data.symbol,
        account: data.account,
        side: data.side,
        strategy: data.strategy,
        changes: data.changes,
        eventTimestamp: data.eventTimestamp,
        isRecoveryEvent: data.isRecoveryEvent,
      },
      clickAction: {
        type: 'navigate',
        payload: {
          path: data.side && data.account 
            ? `/live-trades/chart/${encodeURIComponent(data.symbol)}/${encodeURIComponent(data.side)}/${encodeURIComponent(data.account)}`
            : `/live-trades?filter=${encodeURIComponent(data.symbol)}`,
          state: { from: '/live-trades' } // Fixed: proper back path
        }
      }
    });
  }

  /**
   * Handle notification click action
   */
  handleNotificationClick(notification: Notification, navigate: (path: string, options?: any) => void, currentPath?: string): void {
    if (!notification.clickAction) return;

    switch (notification.clickAction.type) {
      case 'navigate':
        const { path, state } = notification.clickAction.payload;
        // Use current path as the back navigation instead of hardcoded '/notifications'
        const enhancedState = {
          ...state,
          from: currentPath || state?.from || '/', // Use current path if available, fallback to state or home
        };
        navigate(path, { state: enhancedState });
        break;
      
      case 'external':
        const { url } = notification.clickAction.payload;
        window.open(url, '_blank', 'noopener,noreferrer');
        break;
      
      case 'modal':
        // Handle modal opening - could emit custom event or use modal store
        console.log('Modal action not yet implemented:', notification.clickAction.payload);
        break;
      
      default:
        console.warn('Unknown notification click action type:', notification.clickAction.type);
    }
  }

  /**
   * Notification-specific deduplication (more lenient than toast deduplication)
   * Uses a shorter window and unique trade ID consideration
   */
  private notificationEvents: Map<string, { timestamp: number; tradeId?: string }> = new Map();
  private readonly NOTIFICATION_DEDUPLICATION_WINDOW = 15000; // 15 seconds for notifications

  private shouldCreateNotification(event: {
    type: string;
    symbol: string;
    account: string;
    timestamp: number;
    details?: { pnl?: number; tradeId?: string; side?: string; };
  }): boolean {
    const eventKey = `${event.symbol}:${event.account}:${event.type}`;
    const now = Date.now();
    
    // Clean up old events first
    for (const [key, eventData] of this.notificationEvents.entries()) {
      if (now - eventData.timestamp > this.NOTIFICATION_DEDUPLICATION_WINDOW) {
        this.notificationEvents.delete(key);
      }
    }
    
    const existingEvent = this.notificationEvents.get(eventKey);
    
    if (existingEvent) {
      const timeDiff = now - existingEvent.timestamp;
      
      // If we have trade IDs, compare them (unique trades should always be allowed)
      if (event.details?.tradeId && existingEvent.tradeId && 
          event.details.tradeId !== existingEvent.tradeId) {
        // Different trade ID - allow it
        this.notificationEvents.set(eventKey, { 
          timestamp: now, 
          tradeId: event.details.tradeId 
        });
        return true;
      }
      
      if (timeDiff < this.NOTIFICATION_DEDUPLICATION_WINDOW) {
        console.log(`🔄 Duplicate notification prevented: ${event.type} for ${event.symbol}/${event.account} (${timeDiff}ms ago)`);
        return false;
      }
    }
    
    // Register this event
    this.notificationEvents.set(eventKey, { 
      timestamp: now, 
      tradeId: event.details?.tradeId 
    });
    
    return true;
  }

  /**
   * Map notification type to toast type for deduplication service
   */
  private mapNotificationTypeToToastType(type: Notification['type']): any {
    switch (type) {
      case 'position_opened':
        return 'position_opened';
      case 'trade_closed':
        return 'trade_closed';
      case 'position_modified':
        return 'position_modified';
      case 'milestone':
        return 'position_pnl_change'; // Use PnL change type for milestones
      case 'system':
        return 'session_gap_detected'; // Map system notifications to session gap detection
      default:
        return type;
    }
  }

  /**
   * Get category-specific icon class name
   */
  getCategoryIcon(type: Notification['type']): string {
    switch (type) {
      case 'position_opened':
        return 'PlusCircleIcon';
      case 'trade_closed':
        return 'CheckCircleIcon';
      case 'milestone':
        return 'TrophyIcon';
      case 'position_modified':
        return 'AdjustmentsHorizontalIcon';
      case 'system':
        return 'InformationCircleIcon';
      default:
        return 'BellIcon';
    }
  }

  /**
   * Get category-specific color class
   */
  getCategoryColor(type: Notification['type']): string {
    switch (type) {
      case 'position_opened':
        return 'text-blue-500';
      case 'trade_closed':
        return 'text-emerald-500';
      case 'milestone':
        return 'text-yellow-500';
      case 'position_modified':
        return 'text-orange-500';
      case 'system':
        return 'text-gray-500';
      default:
        return 'text-gray-400';
    }
  }
}

// Export singleton instance
export const notificationService = new NotificationService();